import { ITargetingTerm } from '../interfaces/ITargetingTerm';

import { ITargetingGroup } from '../interfaces/ITargetingGroup';
import { Environment } from './Environment';
import { ITargetingFields } from '../interfaces/ITargetingFields';
import { ITargetingProperties } from '../interfaces/ITargetingProperties';
import { ITargetingApplications } from '../interfaces/ITargetingApplications';
import { IApplication, IDefaultValues, IStoredObject, Instance } from '../interfaces/IApplication';
import { FetchProxy } from 'intraactive-sdk-helper';
import { sdkContainerInstance } from '../global/Container';
import { IHttpStatus } from '../interfaces/IHttpStatus';
import DateTimeService from './DateTimeService';
import { ITargetingGroupOptions } from '../interfaces/ITargetingGroupOptions';
import { Helper } from '../Helper';

export interface ITargetingService {
  getTerms(application: string, tenantRef: string, authToken: string): Promise<ITargetingTerm[]>;
}

export class TargetingService implements ITargetingService {

  private apiUrl = "";

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  public getTerms(application: string, tenantRef: string, authToken: string): Promise<ITargetingTerm[]> {
    return new Promise<ITargetingTerm[]>((resolve: (terms: ITargetingTerm[]) => void, reject: (error: any) => void): void => {
      if (application && tenantRef && authToken) {
        new FetchProxy(this.apiUrl + `/api/targeting/${tenantRef}/${application}/terms`, {
          method: "GET",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spTargetingTerms => {

            let targetingTerms: ITargetingTerm[];
            if (!!spTargetingTerms) {
              targetingTerms = <ITargetingTerm[]>spTargetingTerms;
            }

            resolve(targetingTerms);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getAllTerms(environment: any, tenantRef: string, authToken: string): Promise<ITargetingTerm[]> {
    return new Promise<ITargetingTerm[]>((resolve: (terms: ITargetingTerm[]) => void, reject: (error: any) => void): void => {
      if (environment && tenantRef && authToken) {
        new FetchProxy(Environment.getEnvironmentForMainAPI(environment) + `/api/targeting/${tenantRef}/terms`, {
          method: "GET",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spTargetingTerms => {

            let targetingTerms: ITargetingTerm[];
            if (!!spTargetingTerms) {
              targetingTerms = <ITargetingTerm[]>spTargetingTerms;
            }
            Helper.setLocalStorage("IA_targetingTerms", JSON.stringify({
              date: new Date(),
              value: targetingTerms
            }));
            resolve(targetingTerms);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getProperties(environment: any, tenant: string, authToken: string, ignoreCache?: boolean): Promise<{ httpStatus: IHttpStatus, value?: ITargetingProperties[] }> {
    return new Promise<{ httpStatus: IHttpStatus, value?: ITargetingProperties[] }>((resolve: (result: { httpStatus: IHttpStatus, value?: ITargetingProperties[] }) => void): void => {
      if (environment && tenant && authToken) {
        const targetingProperties = localStorage.getItem("IA_targetingProperties");
        if (!ignoreCache && targetingProperties && new DateTimeService().getDatePlusOneHour(new Date(JSON.parse(targetingProperties).date)) > new Date()) {
          console.log("Using cached targeting properties");
          resolve({
            httpStatus: {
              statusCode: 200,
            },
            value: JSON.parse(targetingProperties).value
          });
        } else {
          console.log("Fetching targeting properties from server");
          const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/properties`;
          new FetchProxy(apiUrl, {
            method: "GET",
            headers: { 'AuthToken': authToken }
          })
            .withContainer(sdkContainerInstance)
            .fetch()
            .then((response) => {
              if (!response.ok) {
                resolve({
                  httpStatus: {
                    statusCode: response.status,
                    message: response.statusText
                  }
                });
                return;
              }
              return response.json();
            }).then(spItems => {
              let results: ITargetingProperties[];
              if (!!spItems) {
                results = <ITargetingProperties[]>spItems.result;
              }
              Helper.setLocalStorage("IA_targetingProperties", JSON.stringify({
                date: new Date(),
                value: results
              }));
              resolve({
                httpStatus: {
                  statusCode: 200
                },
                value: results
              });
            })
            .catch(error => {
              console.log(error);
              resolve({
                httpStatus: {
                  statusCode: error.status,
                  message: error.statusText
                }
              });
            });
        }
      } else {
        resolve({
          httpStatus: {
            statusCode: 400,
            message: "Missing parameter"
          }
        });
      }
    });
  }

  public static getGroupsForUser(environment: any, tenant: string, authToken: string, upn: string): Promise<ITargetingGroup[]> {
    return new Promise<ITargetingGroup[]>((resolve: (items: ITargetingGroup[]) => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/user/${upn}/groups`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spItems => {

            let results: ITargetingGroup[];
            if (!!spItems) {
              results = <ITargetingGroup[]>spItems.targetingGroups;
            }
            console.log(results);
            resolve(results);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getGroups(environment: any, tenant: string, authToken: string): Promise<ITargetingGroup[]> {
    return new Promise<ITargetingGroup[]>((resolve: (items: ITargetingGroup[]) => void, reject: (error: any) => void): void => {
      if (environment && tenant) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/groups`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spItems => {

            let results: ITargetingGroup[];
            if (!!spItems) {
              results = <ITargetingGroup[]>spItems.result;
            }

            resolve(results);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getApplication(environment: any, tenant: string, authToken: string, application: "Content Organizer" | "Messages" | "Events" | "Navigator" | "Toolbox" | "App", ignoreCache: boolean): Promise<{ httpStatus: IHttpStatus, value?: ITargetingApplications[] }> {
    return new Promise<{ httpStatus: IHttpStatus, value?: ITargetingApplications[] }>((resolve: (result: { httpStatus: IHttpStatus, value?: ITargetingApplications[] }) => void): void => {
      if (environment && tenant && authToken && application) {
        let localStorageKey;
        switch (application) {
          case "Content Organizer":
            localStorageKey = "IA_targetingApplicationContentOrganizer";
            break;
          case "Messages":
            localStorageKey = "IA_targetingApplicationMessages";
            break;
          case "Events":
            localStorageKey = "IA_targetingApplicationEvents";
            break;
          case "Navigator":
            localStorageKey = "IA_targetingApplicationNavigator";
            break;
          case "Toolbox":
            localStorageKey = "IA_targetingApplicationToolbox";
            break;
          case "App":
            localStorageKey = "IA_targetingApplicationApp";
            break;
        }
        const targetingApplication = localStorage.getItem(localStorageKey);
        if (!ignoreCache && targetingApplication && new DateTimeService().getDatePlusOneHour(new Date(JSON.parse(targetingApplication).date)) > new Date()) {
          console.log("Using cached targeting application settings");
          resolve({
            httpStatus: {
              statusCode: 200,
            },
            value: JSON.parse(targetingApplication).value
          });
        } else {
          console.log("Fetching targeting application settings from server");
          const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant.replace(".sharepoint.com", "").replace(".onmicrosoft.com", "")}/application/${application}/true`;
          new FetchProxy(apiUrl, {
            method: "GET",
            headers: { 'AuthToken': authToken }
          })
            .withContainer(sdkContainerInstance)
            .fetch()
            .then((response) => {
              if (!response.ok) {
                resolve({
                  httpStatus: {
                    statusCode: response.status,
                    message: response.statusText
                  }
                });
                return;
              }
              return response.json();
            }).then(spItems => {
              let results: ITargetingApplications[];
              if (!!spItems) {
                results = <ITargetingApplications[]>spItems.result;
              }
              Helper.setLocalStorage(localStorageKey, JSON.stringify({
                date: new Date(),
                value: results
              }));
              resolve({
                httpStatus: {
                  statusCode: 200,
                },
                value: results
              });
            })
            .catch(error => {
              console.log(error);
              resolve({
                httpStatus: {
                  statusCode: error.status,
                  message: error.statusText
                }
              });
            });
        }
      } else {
        console.log("environment: " + environment);
        console.log("tenant: " + tenant);
        console.log("authToken: " + authToken);
        console.log("application: " + application);
      }
    });
  }

  public static async getTargetingApplication(environment: any, tenant: string, authToken: string, application: string): Promise<ITargetingApplications> {
    let applicationSettings: ITargetingApplications;
    await TargetingService.getApplication(environment, tenant, authToken, application === "Messages" ? "Messages" : "Events", false).then((response: { httpStatus: IHttpStatus, value?: ITargetingApplications[] }) => {
      if (response.httpStatus.statusCode === 200 && response?.value?.length > 0) {
        applicationSettings = response.value[0];
        let application: IApplication;
        const appData = <IApplication>JSON.parse(response.value[0].defaultValues);
        let defaultValues: IDefaultValues;
        if (!appData || Array.isArray(appData)) {
          const list: any[] = [];
          defaultValues = {
            isEnable: false,
            type: "profileData",
            values: list,
            allValues: false
          };
          application = {
            id: undefined,
            title: response.value[0].title,
            name: response.value[0].title,
            instances: [],
            default: defaultValues
          };
        } else {
          application = appData;
          application.default.type = response?.value?.length > 0 && response?.value[0].applicationSettings?.toLocaleLowerCase() === "profile data" ? "profileData" : "userGroups";
        }
        applicationSettings.application = application;
      }
    });
    return applicationSettings;
  }

  public static createGroup(environment: any, tenant: string, authToken: string, item: ITargetingGroup): Promise<number> {
    return new Promise<number>((resolve: (status: number) => void): void => {
      if (environment && tenant && authToken && item) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/groups`;
        new FetchProxy(apiUrl, {
          method: "POST",
          headers: { 'AuthToken': authToken },
          body: JSON.stringify(item)
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(response.status);
              return;
            }
            resolve(response.status);
          })
          .catch(error => {
            console.log(error);
            resolve(500);
          });
      } else {
        resolve(500);
        console.log("Missing parameters for createGroup!");
      }
    });
  }

  public static updateGroup(environment: any, tenant: string, authToken: string, item: ITargetingGroup, id: any): Promise<number> {
    return new Promise<number>((resolve: (status: number) => void): void => {
      if (environment && tenant && authToken && item) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/groups/${id}/true`;
        new FetchProxy(apiUrl, {
          method: "PUT",
          headers: { 'AuthToken': authToken },
          body: JSON.stringify(item)
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(response.status);
              return;
            }
            resolve(response.status);
          })
          .catch(error => {
            console.log(error);
            resolve(500);
          });
      } else {
        resolve(500);
        console.log("Missing parameters for updateGroup!");
      }
    });
  }

  public static deleteGroup(environment: any, tenant: string, authToken: string, id: any): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken && id) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/groups/${id}`;
        new FetchProxy(apiUrl, {
          method: "DELETE",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            resolve();
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static updateTermFields(item: any, application: string, environment: "Development" | "Test" | "Production", token: string, tenant: string, instance: string, values: ITargetingFields[]): Promise<IHttpStatus> {
    return new Promise<IHttpStatus>((resolve: (httpStatus: IHttpStatus) => void): void => {
      if (environment && tenant && item && application && instance && values && token) {
        const apiUrl = `${Environment.getEnvironmentForMainAPI(environment)}/api/targeting/${tenant}.onmicrosoft.com/${application}/${instance}/item?id=${item.id}&targetContent=${item.iaTargetingTargetContent}&targetingOperator=${item.iaTargetingOperator}`;
        const body: { [k: string]: any } = {};
        // Formating values to be saved
        const keys = Object.keys(values);
        keys.forEach((key) => {
          // Removing the "IA" from the beggining
          const fieldName = key.substring(2, key.length);
          body[fieldName] = [];
          values[key].forEach((keyValue) => {
            body[fieldName].push(keyValue.Label ? keyValue.Label : keyValue.label);
          });
        });
        new FetchProxy(apiUrl, { method: "POST", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: JSON.stringify(body) })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return resolve({
              statusCode: 200
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              statusCode: error.status,
              message: error.statusText
            });
          });
      } else {
        resolve({
          statusCode: 400,
          message: "Missing parameter"
        });
      }
    });
  }


  public static getTermsAvailable(environment: "Development" | "Test" | "Production", tenant: string, token: string): Promise<any[]> {
    return new Promise<any[]>((resolve: (termsAvailable: any[]) => void, reject: (error: any) => void): void => {
      if (environment && tenant && token) {
        let apiUrl = Environment.getEnvironmentForMainAPI(environment);
        tenant = tenant.replace(".onmicrosoft.com", ".sharepoint.com");
        if (tenant.lastIndexOf('.sharepoint.com') < 0) {
          tenant = tenant + '.sharepoint.com';
        }
        const body = {
          tenantRef: tenant,
          siteRef: "IntraActive-Targeting"
        };

        apiUrl = apiUrl + `/api/targeting/getTermsAvailable?tenantRef=${encodeURIComponent(body.tenantRef)}&siteRef=${encodeURIComponent(body.siteRef)}`;

        new FetchProxy(apiUrl, { method: "GET", credentials: "same-origin", headers: { 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }

            return response.json();
          }).then(result => {
            resolve(result);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static syncTerms(environment: "Development" | "Test" | "Production", terms: Array<{ id: string, name: string }>, tenantRef: string, token: string): Promise<any> {
    return new Promise<any>((resolve: (response: string) => void, reject: (error: any) => void): void => {
      if (environment && tenantRef && token && terms) {
        let apiUrl = Environment.getEnvironmentForMainAPI(environment);
        const body = {
          tenantRef: tenantRef,
          siteRef: "IntraActive-Targeting",
        };
        apiUrl = apiUrl + `/api/targeting/validateandsyncterms?tenantRef=${encodeURIComponent(body.tenantRef)}&siteRef=${encodeURIComponent(body.siteRef)}&selectedTerms=${encodeURIComponent(JSON.stringify(terms))}`;
        new FetchProxy(apiUrl, { method: "POST", credentials: "same-origin", headers: { 'AuthToken': token } })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(result => {
            resolve(result);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getDefaultTerms(environment: any, tenant: string, authToken: string): Promise<any[]> {
    return new Promise<any[]>((resolve: (items: any[]) => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/terms`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spItems => {

            let results: any[];
            if (!!spItems) {
              results = <any[]>spItems.result;
            }

            resolve(results);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static getProfileDataProperties(environment: any, tenant: string, authToken: string): Promise<any[]> {
    return new Promise<any[]>((resolve: (items: any[]) => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken) {
        const apiUrl = Environment.getEnvironmentForMainAPI(environment) + `/api/${tenant + '.sharepoint.com'}/users/userproperties`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: { 'AuthToken': authToken, 'Content-Type': 'application/json' }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spItems => {

            let results: any[];
            if (!!spItems) {
              results = <any[]>spItems.map(item => {
                return item.key;
              });
            }

            resolve(results);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }


  public static createProperty(displayName: string, userProfileProperty: string, termSetId: string, termGroupId: string, environment: any, tenant: string, authToken: string): Promise<boolean> {
    return new Promise<boolean>((resolve: (created: boolean) => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken && displayName && userProfileProperty && termSetId && termGroupId) {
        let apiUrl: string = "";
        const body = { FieldName: displayName, UserProfileProp: userProfileProperty, TermSetId: termSetId, TermGroupId: termGroupId };
        apiUrl = Environment.getEnvironmentForMainAPI(environment) + `/api/targeting/${tenant}/field`;
        new FetchProxy(apiUrl, { method: "POST", credentials: "same-origin", headers: { 'AuthToken': authToken, 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.ok;
          }).then(result => {
            resolve(result);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static updateProperty(updateProperty: any, environment: any, tenant: string, authToken: string): Promise<boolean> {
    return new Promise<boolean>((resolve: (created: boolean) => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken && updateProperty) {
        let apiUrl: string = "";
        apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/properties/${updateProperty.id}`;
        new FetchProxy(apiUrl, { method: "PUT", credentials: "same-origin", headers: { 'AuthToken': authToken, 'Content-Type': 'application/json' }, body: JSON.stringify(updateProperty) })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.ok;
          }).then(result => {
            resolve(result);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }


  public static getApplicationsTargetSettings(environment: any, tenant: string, authToken: string): Promise<any[]> {
    return new Promise<any[]>((resolve: (items: any[]) => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/applications`;
        new FetchProxy(apiUrl, {
          method: "GET",
          headers: { 'AuthToken': authToken }
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            return response.json();
          }).then(spItems => {

            let results: any[];
            if (!!spItems && spItems.result && spItems.result.length > 0) {
              results = spItems.result.map(app => {
                const appData = <IApplication>JSON.parse(app.defaultValues);
                let defaultValues: IDefaultValues;
                let application: IApplication;
                if ((!appData || Array.isArray(appData))) {
                  defaultValues = {
                    isEnable: false,
                    type: "profileData",
                    values: [],
                    allValues: false
                  };
                  application = {
                    id: app.id,
                    title: app.title,
                    name: app.title,
                    instances: [],
                    default: defaultValues
                  };
                } else {
                  if (typeof appData.default.allValues === 'string') {
                    appData.default.allValues = (<string>appData.default.allValues).toLocaleLowerCase() === "true" ? true : false;
                  }
                  application = appData;

                  if (application) {
                    if (application.default && typeof (application.default.isEnable) === "string") {
                      application.default.isEnable = (/true/i).test(application.default.isEnable);
                    }

                    if (application.instances && application.instances.length > 0) {
                      application.instances.forEach((i: Instance) => {
                        if (i && typeof (i.isEnable) === "string") {
                          i.isEnable = (/true/i).test((<string>i.isEnable).toString());
                        }
                      });
                    }

                  }
                  if (application?.default) {
                    application.default.type = app && app?.applicationSettings?.toLocaleLowerCase() === "profile data" ? "profileData" : "userGroups";
                  }
                }
                return application;
              });
            }

            resolve(results);
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  //   private static escapeSpecialChars(jsonString: string): string {

  //     return jsonString.replace(/\n/g, "\\n")
  //         .replace(/\r/g, "\\r")
  //         .replace(/\t/g, "\\t")
  //         .replace(/\f/g, "\\f")
  //         .replace(/'/g, '"');

  // }

  private static getTermsFromlist(listTerms: any[], list: IStoredObject[], property: any) {
    listTerms.forEach(term => {
      list.push({ id: term.Id, label: term.DefaultLabel, value: true, field: property.title });
      if (term.ChildTerms && term.ChildTerms.length > 0) {
        this.getTermsFromlist(term.ChildTerms, list, property);
      }
    });
    return list;
  }

  public static updateApplicationsTargetSettings(environment: any, tenant: string, authToken: string, item: any, id: any): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken && item && id) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/applications/${id}`;
        new FetchProxy(apiUrl, {
          method: "PUT",
          headers: { 'AuthToken': authToken },
          body: JSON.stringify(item)
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            resolve();
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static updateApplicationsGroupsAndProfileData(environment: any, tenant: string, authToken: string, item: any[]): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (error: any) => void): void => {
      if (environment && tenant && authToken && item) {
        const apiUrl = Environment.getEnvironmentForTargetingAPI(environment) + `/${tenant}/applications/`;
        new FetchProxy(apiUrl, {
          method: "PUT",
          headers: { 'AuthToken': authToken },
          body: JSON.stringify(item)
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response);
              return;
            }
            resolve();
          })
          .catch(error => {
            console.log(error);
            reject(null);
          });
      } else {
        reject(null);
      }
    });
  }

  public static updateTargetingGroup(termGroup: any, application: string, environment: "Development" | "Test" | "Production", token: string, tenant: string, instance: string, id: number, fieldName: string): Promise<IHttpStatus> {
    return new Promise<IHttpStatus>((resolve: (httpStatus: IHttpStatus) => void): void => {
      if (environment && tenant && token && instance && termGroup && application) {
        const apiUrl = `${Environment.getEnvironmentForTargetingAPI(environment)}/${tenant}/targetingGroup/${id}`;
        const body = this.getTargetingGroupOptions(termGroup, application, instance, fieldName);
        new FetchProxy(apiUrl, { method: "PUT", headers: { 'Content-Type': 'application/json', 'AuthToken': token }, body: JSON.stringify(body) })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              throw (response);
            }
            return resolve({
              statusCode: 200
            });
          })
          .catch(error => {
            console.log(error);
            resolve({
              statusCode: error.status,
              message: error.statusText
            })
          });
      } else {
        resolve({
          statusCode: 400,
          message: "Missing parameter"
        });
      }
    });
  }

  public static getTargetingGroupOptions(termGroup: any, application: string, instance: string, fieldName: string): ITargetingGroupOptions {
    const instanceListName = !instance || (application === "Messages" && instance.toLowerCase() === "default") ? application : `${instance} - ${application}`;
    const instanceInternalName = !instance || (application === "Messages" && instance.toLowerCase() === "default") ? "IA" + application : `${instance} - ${application}`;
    // Formating values to be saved
    return {
      Field: fieldName,
      Value: termGroup,
      Site: "IntraActive-" + application,
      ListName: instanceListName,
      ListInternalName: instanceInternalName
    };
  }
}
